import axios from 'axios'
import { useToast } from 'vue-toastification'

const toast = useToast()
const CLIENT_SESSION_STORAGE_KEY = 'bwai_client_session_id'

const generateSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  const random = Math.random().toString(16).slice(2, 10)
  const stamp = Date.now().toString(16).slice(-8)
  return `${stamp}-${random}-4${random.slice(0, 3)}-a${random.slice(3, 6)}-${stamp}${random}`.slice(0, 36)
}

const getOrCreateClientSessionId = () => {
  try {
    const existing = sessionStorage.getItem(CLIENT_SESSION_STORAGE_KEY)
    if (existing) return existing

    const created = generateSessionId()
    sessionStorage.setItem(CLIENT_SESSION_STORAGE_KEY, created)
    return created
  } catch (_) {
    return generateSessionId()
  }
}

// Create axios instance
const api = axios.create({
  baseURL: '/api/bwai',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('bwai_token')
    const clientSessionId = getOrCreateClientSessionId()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    config.headers['X-Session-ID'] = clientSessionId
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const message = error.response?.data?.error || error.message || 'An error occurred'
    
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      localStorage.removeItem('bwai_token')
      localStorage.removeItem('bwai_user')
      localStorage.removeItem('bwai_tenant')
      window.location.href = '/login'
      return Promise.reject(error)
    }
    
    if (error.response?.status === 429) {
      toast.error('Rate limit exceeded. Please try again later.')
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.')
    } else if (error.response?.status >= 400) {
      toast.error(message)
    } else if (error.code === 'NETWORK_ERROR') {
      toast.error('Network error. Please check your connection.')
    }
    
    return Promise.reject(error)
  }
)

export default api
